import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { transcribeAudio } from "@/lib/api";

/**
 * Hold-to-dictate mic. Records with MediaRecorder, ships the blob to Whisper,
 * hands the transcript back via onTranscript.
 */
export const MicButton = ({ onTranscript, label = "Dictate", testId = "mic-button" }) => {
  const [state, setState] = useState("idle"); // idle | recording | working
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Your browser can't record audio");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopTracks();
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 1200) {
          setState("idle");
          toast.error("Recording too short — hold it a beat longer");
          return;
        }
        setState("working");
        try {
          const ext = type.includes("mp4") ? "mp4" : "webm";
          const r = await transcribeAudio(blob, `recording.${ext}`);
          if (r.text) {
            onTranscript?.(r.text);
            toast.success("Transcribed");
          } else {
            toast.error("Didn't catch that — try again");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Transcription failed");
        } finally {
          setState("idle");
        }
      };
      recorderRef.current = rec;
      rec.start();
      setState("recording");
    } catch {
      toast.error("Microphone permission denied");
      stopTracks();
      setState("idle");
    }
  };

  const stop = () => {
    try { recorderRef.current?.stop(); } catch { /* already stopped */ }
  };

  const busy = state === "working";
  const recording = state === "recording";

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={recording ? stop : start}
      disabled={busy}
      className={`shrink-0 flex items-center gap-2 border px-3 py-3 font-mono text-[10px] tracking-[0.25em] uppercase transition-all disabled:opacity-50 ${
        recording
          ? "border-[#fb7185] text-[#fb7185] bg-[#fb7185]/10 animate-pulse"
          : "border-zinc-800 text-zinc-300 hover:border-[#60a5fa] hover:text-[#60a5fa]"
      }`}
      title={recording ? "Stop and transcribe" : "Speak instead of typing"}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : recording ? <Square className="h-3.5 w-3.5" />
        : <Mic className="h-3.5 w-3.5" />}
      <span className="hidden sm:inline">{busy ? "Reading…" : recording ? "Stop" : label}</span>
    </button>
  );
};

export default MicButton;
